/**
 * Pure logic for the admin support wiki — frontmatter mapping, cross-link
 * resolution, and list grouping. Kept dependency-free (no DOM, no Prisma) so
 * it's directly unit-testable per CLAUDE.md's "test the logic functions, not
 * the components."
 */

export const WIKI_BASE_PATH = '/help'

/** Article statuses, from frontmatter `status:`. Only a human sets `reviewed`. */
export const WIKI_ARTICLE_STATUSES = ['stub', 'drafted', 'needs-review', 'reviewed'] as const

/** The only status production shows, and the one the deploy-time import
 *  never lets an unreviewed edit replace (GLASS-575). */
export const REVIEWED_WIKI_ARTICLE_STATUS = 'reviewed'

export interface WikiArticleImportData {
  slug: string
  title: string
  summary: string
  type: string
  portal: string
  area: string | null
  subarea: string | null
  roles: string[]
  displayOrder: number
  keywords: string[]
  questions: string[]
  relatedRoutes: string[]
  relatedArticles: string[]
  prerequisites: string[]
  sourceFiles: string[]
  body: string
  status: string
  verifiedAgainstGitRef: string | null
  verifiedAgainstDate: Date | null
  reviewedBy: string | null
  reviewedAt: Date | null
  openQuestions: string[]
}

const REQUIRED_STRING_FIELDS = [
  'title',
  'slug',
  'summary',
  'type',
  'portal',
  'status',
] as const

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function toStringOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null
  return typeof value === 'string' ? value : String(value)
}

function toDateOrNull(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return value
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

/**
 * Maps a gray-matter-parsed frontmatter object + body to WikiArticle
 * create/update data. Throws (does not default) on a missing required field
 * or a slug that doesn't match its file path — per
 * content/wiki/_authoring/FRONTMATTER-SPEC.md, a missing key is an error.
 */
export function mapFrontmatterToWikiArticle(
  frontmatter: Record<string, unknown>,
  body: string,
  relativePathWithoutExtension: string
): WikiArticleImportData {
  const missing = REQUIRED_STRING_FIELDS.filter(
    (field) =>
      typeof frontmatter[field] !== 'string' || frontmatter[field] === ''
  )
  if (missing.length > 0) {
    throw new Error(
      `${relativePathWithoutExtension}: missing required frontmatter field(s): ${missing.join(', ')}`
    )
  }

  const slug = frontmatter.slug as string
  if (slug !== relativePathWithoutExtension) {
    throw new Error(
      `${relativePathWithoutExtension}: frontmatter slug "${slug}" does not match its file path`
    )
  }

  const verifiedAgainst = (frontmatter.verified_against ?? {}) as Record<
    string,
    unknown
  >

  return {
    slug,
    title: frontmatter.title as string,
    summary: (frontmatter.summary as string).trim(),
    type: frontmatter.type as string,
    portal: frontmatter.portal as string,
    area: toStringOrNull(frontmatter.area),
    subarea: toStringOrNull(frontmatter.subarea),
    roles: toStringArray(frontmatter.roles),
    displayOrder:
      typeof frontmatter.order === 'number' ? frontmatter.order : 0,
    keywords: toStringArray(frontmatter.keywords),
    questions: toStringArray(frontmatter.questions),
    relatedRoutes: toStringArray(frontmatter.related_routes),
    relatedArticles: toStringArray(frontmatter.related_articles),
    prerequisites: toStringArray(frontmatter.prerequisites),
    sourceFiles: toStringArray(frontmatter.source_files),
    body: body.trim(),
    status: frontmatter.status as string,
    verifiedAgainstGitRef: toStringOrNull(verifiedAgainst.git_ref),
    verifiedAgainstDate: toDateOrNull(verifiedAgainst.date),
    reviewedBy: toStringOrNull(frontmatter.reviewed_by),
    reviewedAt: toDateOrNull(frontmatter.reviewed_at),
    openQuestions: toStringArray(frontmatter.open_questions),
  }
}

export type WikiLinkResolution =
  | { kind: 'route'; to: string }
  | { kind: 'external'; href: string }
  | { kind: 'anchor'; href: string }
  | { kind: 'unresolved'; original: string }

function normalizeRelativePath(base: string, relative: string): string {
  const stack = base ? base.split('/') : []
  for (const part of relative.split('/')) {
    if (part === '' || part === '.') continue
    if (part === '..') stack.pop()
    else stack.push(part)
  }
  return stack.join('/')
}

/**
 * Resolves a markdown link href relative to the article it appears in.
 * Also recovers the `../sibling`/bare-sibling link forms wave 1 shipped
 * with (they should be full slugs per ARTICLE-TEMPLATE.md, but aren't) by
 * falling back to placing the link's last path segment directly in the
 * current article's directory rather than treating `..` as "leave it."
 */
export function resolveWikiArticleHref(
  href: string,
  currentSlug: string,
  knownSlugs: ReadonlySet<string>
): WikiLinkResolution {
  if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) {
    return { kind: 'external', href }
  }
  if (href.startsWith('#')) {
    return { kind: 'anchor', href }
  }
  if (href.startsWith('/')) {
    return { kind: 'route', to: href }
  }

  const currentDir = currentSlug.includes('/')
    ? currentSlug.slice(0, currentSlug.lastIndexOf('/'))
    : ''

  const properlyResolved = normalizeRelativePath(currentDir, href)
  if (knownSlugs.has(properlyResolved)) {
    return { kind: 'route', to: `${WIKI_BASE_PATH}/${properlyResolved}` }
  }

  const basename = href.split('/').filter(Boolean).pop() ?? href
  const recovered = currentDir ? `${currentDir}/${basename}` : basename
  if (knownSlugs.has(recovered)) {
    return { kind: 'route', to: `${WIKI_BASE_PATH}/${recovered}` }
  }

  if (knownSlugs.has(href)) {
    return { kind: 'route', to: `${WIKI_BASE_PATH}/${href}` }
  }

  return { kind: 'unresolved', original: href }
}

export interface WikiArticleListItem {
  slug: string
  title: string
  summary: string
  area: string | null
  subarea: string | null
  displayOrder: number
  status: string
  type: string
  keywords: string[]
  questions: string[]
}

/** Turns a kebab-case frontmatter area/subarea value ("getting-started",
 *  "funding-sources") into a display label ("Getting Started", "Funding
 *  Sources") for nav headings. The `Concepts` bucket label is already
 *  display-ready and passes through unchanged. */
export function humanizeWikiLabel(slug: string): string {
  return slug
    .split('-')
    .map((word) => (word.length === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join(' ')
}

export interface WikiSubareaGroup {
  subarea: string | null
  articles: WikiArticleListItem[]
}

export interface WikiAreaGroup {
  area: string
  subareas: WikiSubareaGroup[]
}

/** Portal-agnostic concepts/ articles have area: null — bucketed here rather
 *  than dropped, and always rendered last since it's reference material, not
 *  a workflow area. */
const CONCEPTS_BUCKET_LABEL = 'Concepts'

/** Raw frontmatter `area` value for the Getting Started section — always
 *  rendered first since it's the reader's onboarding entry point, not
 *  alphabetically with the workflow areas. */
const GETTING_STARTED_AREA = 'getting-started'

export function groupWikiArticlesByArea(
  articles: WikiArticleListItem[]
): WikiAreaGroup[] {
  const areaMap = new Map<string, Map<string | null, WikiArticleListItem[]>>()

  for (const article of articles) {
    const areaLabel = article.area ?? CONCEPTS_BUCKET_LABEL
    if (!areaMap.has(areaLabel)) areaMap.set(areaLabel, new Map())
    const subareaMap = areaMap.get(areaLabel)!
    if (!subareaMap.has(article.subarea)) subareaMap.set(article.subarea, [])
    subareaMap.get(article.subarea)!.push(article)
  }

  const byDisplayOrder = (a: WikiArticleListItem, b: WikiArticleListItem) =>
    a.displayOrder - b.displayOrder

  const areaNames = [...areaMap.keys()]
    .filter((name) => name !== CONCEPTS_BUCKET_LABEL && name !== GETTING_STARTED_AREA)
    .sort()
  if (areaMap.has(GETTING_STARTED_AREA)) areaNames.unshift(GETTING_STARTED_AREA)
  if (areaMap.has(CONCEPTS_BUCKET_LABEL)) areaNames.push(CONCEPTS_BUCKET_LABEL)

  return areaNames.map((area) => {
    const subareaMap = areaMap.get(area)!
    const subareaKeys = [...subareaMap.keys()].sort((a, b) => {
      if (a === null) return -1
      if (b === null) return 1
      return a.localeCompare(b)
    })
    return {
      area,
      subareas: subareaKeys.map((subarea) => ({
        subarea,
        articles: subareaMap.get(subarea)!.sort(byDisplayOrder),
      })),
    }
  })
}
